import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import LoaderComponent from 'components/LoaderComponent';
import PrescriptionMedicamentsModal from 'components/treatments/segments/PrescriptionMedicamentsModal';
import { FormApi } from 'final-form';
import moment from 'moment';
import React, { Component } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Accordion, Button, Grid, Icon, Modal, Table } from 'semantic-ui-react';
import { getMedicamentsWithDosages } from 'services/medicamentServices';
import styled from 'styled-components';
import { PrescriptionDto, PrescriptionMedicament, PrescriptionMedicamentDto } from 'ts-types/api.types';

const StyledBorderGrid = styled(Grid)`
  border-bottom: 1px solid rgba(34, 36, 38, .1);
`;

const addButton = {
  marginBottom: 10,
  marginTop: 10
};

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  activeIndex?: number;
  form: FormApi;
  treatmentDate: string;
  readOnly: boolean;
  parentInfoMissing: boolean;
}

interface State {
  medicaments: PrescriptionMedicamentDto[]
  activeIndex?: number;
  editPrescription: boolean;
  editPrescriptionIndex?: number;
  prescriptionCopy?: Partial<PrescriptionDto>;
  requiredDataLoaded: boolean;
}

class PrescriptionsSegment extends Component<Props, State> {

  cancelTokenSource = axios.CancelToken.source();

  constructor(props: Props) {
    super(props);

    const {activeIndex} = props;

    this.state = {
      medicaments: [],
      activeIndex: activeIndex,
      editPrescription: false,
      requiredDataLoaded: false
    }
  }

  loadRequiredData = async () => {
    const medicaments = await getMedicamentsWithDosages(this.cancelTokenSource);
    this.setState({
      medicaments,
      requiredDataLoaded: true
    });
  };

  // validateFields = (unfit: Partial<Unfit>): boolean => {
  //
  //   let errors: Array<boolean> = [];
  //
  //   const mustBeNumber = !((unfit.percent || unfit.percent === 0) && isValueValidNumber(unfit.percent));
  //
  //   errors[0] = unfit.fromDate == "";
  //   errors[1] = unfit.toDate == "";
  //   errors[2] = mustBeNumber;
  //
  //   this.setState({
  //     errors: errors
  //   });
  //
  //   return errors.includes(true);
  // };

  openPrescriptionSegment = (e: any, titleProps: any) => {

    const {
      requiredDataLoaded,
      activeIndex
    } = this.state;

    const {index} = titleProps;
    const newIndex = activeIndex === index ? -1 : index;

    this.setState({activeIndex: newIndex});

    if (!requiredDataLoaded) {
      this.loadRequiredData();
    }
  };

  addPrescription = () => {

    const {treatmentDate, form} = this.props;

    const newPresMed: Partial<PrescriptionMedicament> = {
      medicamentId: undefined,
      dosage: "",
      remarks: ""
    }

    const newPrescription = {
      prescriptionDate: this.props.options.workDate,
      medicaments: [newPresMed],
      sentMail: false
    };

    const prescriptionsLength: number = form.getState().values.prescriptions.length;
    form.mutators.push("prescriptions", newPrescription);
    this.setState({
      editPrescription: true,
      editPrescriptionIndex: prescriptionsLength
    })
  };

  savePrescription = () => {
    this.setState({
      editPrescription: false,
      editPrescriptionIndex: undefined
    });
  };

  cancelEditPrescription = (fields: any, index: number) => {

    const {prescriptionCopy} = this.state;

    this.setState({
      editPrescription: false,
      editPrescriptionIndex: undefined,
      prescriptionCopy: undefined
    });

    fields.remove(index);
    if (prescriptionCopy) {
      fields.insert(index, prescriptionCopy);
    }
  };

  editPrescription = (index: number, field: Partial<PrescriptionDto>, fields: any): void => {

    const {editPrescription, editPrescriptionIndex, prescriptionCopy} = this.state;

    if (editPrescription) {

      fields.remove(editPrescriptionIndex);
      fields.insert(editPrescriptionIndex, prescriptionCopy);

      this.setState({
        editPrescriptionIndex: index,
        prescriptionCopy: field
      });
    } else {
      this.setState({
        editPrescription: true,
        editPrescriptionIndex: index,
        prescriptionCopy: field
      })
    }
  };

  render(): React.ReactNode {

    const {
      getMessage,
      readOnly,
      parentInfoMissing
    } = this.props;

    const {
      requiredDataLoaded,
      activeIndex,
      editPrescription,
      editPrescriptionIndex
    } = this.state;

    return (
        <StyledBorderGrid>
          <Accordion fluid>
            <Accordion.Title active={activeIndex === 0} index={0} onClick={this.openPrescriptionSegment}>
              <h4><Icon name="dropdown"/>{getMessage('prescription.title')}</h4>
            </Accordion.Title>
            <Accordion.Content active={activeIndex === 0} style={{paddingBottom: 20}}>

              {!requiredDataLoaded && <LoaderComponent message={"..."}/>}

              {requiredDataLoaded && <>
                <PraxFormGrid>
                  <Grid.Row>
                    <Grid.Column>
                      <Table celled selectable>
                        <Table.Header>
                          <Table.Row>
                            <Table.HeaderCell>{getMessage('date')}</Table.HeaderCell>
                            <Table.HeaderCell>{getMessage('medicaments')}</Table.HeaderCell>
                            <Table.HeaderCell>{getMessage('actions')}</Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          <FieldArray name="prescriptions">
                            {({fields}) =>
                                fields.map((field, index: number) => {
                                  return (
                                      <Table.Row key={index}>
                                        <Table.Cell width={5}>
                                          {moment(fields.value[index].prescriptionDate).format("DD.MM.YYYY")}
                                        </Table.Cell>
                                        <Table.Cell width={5}>
                                          ({fields.value[index].medicaments.length})
                                        </Table.Cell>
                                        <Table.Cell width={2}>
                                          <EditActionButton
                                              hoverMessage={getMessage('edit')}
                                              onConfirm={() => this.editPrescription(index, fields.value[index], fields)}
                                              disabled={fields.value[index].mailSent}
                                          />
                                          <DeleteActionButton
                                              hoverMessage={getMessage("msgDelete")}
                                              onConfirm={() => fields.remove(index)}
                                              disabled={readOnly || fields.value[index].mailSent}
                                          />
                                        </Table.Cell>
                                      </Table.Row>
                                  )
                                })
                            }
                          </FieldArray>
                        </Table.Body>
                      </Table>
                    </Grid.Column>
                  </Grid.Row>
                  {parentInfoMissing
                      ? <span style={{color: 'darkred'}}>
                        {getMessage("treatmentInfo.prescription.missingParentInfo.message")}
                      </span>
                      : <Grid.Row>
                        <Grid.Column>
                          <Button
                              type="button"
                              compact
                              style={addButton}
                              onClick={() => this.addPrescription()}
                              disabled={editPrescription || readOnly}
                          >
                            {getMessage('button.add')}
                          </Button>
                        </Grid.Column>
                      </Grid.Row>
                  }
                </PraxFormGrid>

                <FieldArray name="prescriptions">
                  {({fields}) =>
                      fields.map((field, index: number) => {
                        if (fields.value[index] !== undefined) {
                          return (editPrescription && editPrescriptionIndex === index) &&
                              this.renderPrescriptionForm(field, index, fields)
                        }
                      })
                  }
                </FieldArray>
              </>
              }
            </Accordion.Content>
          </Accordion>
        </StyledBorderGrid>
    );
  }

  renderPrescriptionForm = (field: string, index: number, fields: any) => {

    const {getMessage} = this.props;

    const {
      medicaments,
      editPrescription,
      editPrescriptionIndex
    } = this.state;

    return (
        <Modal
            size="large"
            key={index}
            open={editPrescription && editPrescriptionIndex === index}
            onClose={() => this.cancelEditPrescription(fields, index)}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage('prescription.title')}</Modal.Header>
          <FormModalContentContainer>
            <PrescriptionMedicamentsModal
                field={field}
                fields={fields}
                form={this.props.form}
                prescriptionIx={index}
                medicaments={medicaments}
                savePrescription={this.savePrescription}
                cancelEditPrescription={this.cancelEditPrescription}
            />
          </FormModalContentContainer>
        </Modal>
    );
  }

}

export default withAuthContext(withMessages(PrescriptionsSegment));
